import { useState } from "react";
import { Bot, ShieldCheck, FileText } from "lucide-react";
import { DashFrame, LegendDots, StatusPill, usePalette } from "./kit";

const previousProcess = ["Download exports", "Copy & paste", "Clean by hand", "Rebuild tables", "Re-check totals", "Email the team"];

const groups = [
  {
    name: "Inputs",
    nodes: [
      { id: "in1", label: "Supplier invoices", detail: "Invoices arrive by email or export. In the automated flow they are collected from their source location on a schedule instead of downloaded by hand." },
      { id: "in2", label: "Contract terms & buy prices", detail: "The reference data the report checks against: contracted rates, rebate terms and recorded costs." },
    ],
  },
  {
    name: "Processing",
    nodes: [
      { id: "p1", label: "PowerShell: collect & merge", detail: "A scheduled PowerShell script collects the source files, merges them and standardises columns and formats. Replaces manual download, copy-paste and cleaning steps.", replaced: true },
      { id: "p2", label: "Build report tables", detail: "The script assembles the report tables exactly as the manual version did — same structure, no hand rebuilding.", replaced: true },
    ],
  },
  {
    name: "Validation",
    nodes: [
      { id: "v1", label: "Automated checks", detail: "Row counts, totals and key thresholds are validated automatically before the report is released. Replaces manual re-checking.", replaced: true },
      { id: "v2", label: "Exception queue", detail: "Anything that fails a check is held in a queue for human review rather than sent out incorrect. This control is retained.", control: true },
    ],
  },
  {
    name: "Outputs",
    nodes: [
      { id: "o1", label: "Refreshed report", detail: "The report is rebuilt automatically on schedule with the validated data." },
      { id: "o2", label: "Distribution", detail: "The script distributes the report to the team on schedule. Replaces the manual email step.", replaced: true },
    ],
  },
];

function nodeStyle(n, isSelected, c) {
  if (isSelected) return { background: c.navy, borderColor: c.navy, color: "#fff" };
  if (n.replaced) return { background: `${c.blue}14`, borderColor: c.blue, color: c.navy };
  if (n.control) return { background: `${c.teal}14`, borderColor: c.teal, color: c.teal };
  return { background: "#fff", borderColor: "#DED5EC", color: "#28272B" };
}

export default function AutomationFlow() {
  const [selected, setSelected] = useState("p1");
  const c = usePalette();
  const node = groups.flatMap((g) => g.nodes).find((n) => n.id === selected);
  const replaced = groups.flatMap((g) => g.nodes).filter((n) => n.replaced).length;
  const controls = groups.flatMap((g) => g.nodes).filter((n) => n.control);

  return (
    <DashFrame
      title="Reporting automation workflow"
      note="Illustrative reconstruction using representative sample data. No time savings are claimed — only the workflow is shown."
      testid="dash-automation"
    >
      <div className="mb-5 flex flex-wrap gap-2">
        <StatusPill color={c.blue} icon={Bot} testid="automation-replaced-pill">{replaced} manual steps replaced</StatusPill>
        {controls.map((k) => (
          <StatusPill key={k.id} color={c.teal} icon={ShieldCheck}>Control retained: {k.label.toLowerCase()}</StatusPill>
        ))}
      </div>

      <div className="mb-6 rounded-xl border border-dashed p-4" style={{ borderColor: c.slate }} data-testid="automation-previous-process">
        <p className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-[0.16em]" style={{ color: c.slate }}>
          Previous manual process (replaced)
        </p>
        <div className="flex flex-wrap items-center gap-1.5">
          {previousProcess.map((step, i) => (
            <span key={step} className="flex items-center gap-1.5">
              <span className="rounded-full border px-3 py-1 text-[11px] font-medium" style={{ borderColor: c.slate, color: c.slate, textDecoration: "line-through" }}>
                {step}
              </span>
              {i < previousProcess.length - 1 && <span aria-hidden="true" style={{ color: c.slate }}>→</span>}
            </span>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        {groups.map((g) => (
          <div key={g.name}>
            <p className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-[0.16em]" style={{ color: c.navy }}>{g.name}</p>
            <div className="space-y-2">
              {g.nodes.map((n) => {
                const isSel = selected === n.id;
                return (
                  <button
                    key={n.id}
                    data-testid={`automation-node-${n.id}`}
                    onClick={() => setSelected(n.id)}
                    aria-pressed={isSel}
                    className="w-full rounded-xl border p-3 text-left text-xs font-medium leading-snug transition-all duration-150 hover:-translate-y-0.5"
                    style={nodeStyle(n, isSel, c)}
                  >
                    <span className="flex items-start gap-1.5">
                      {n.replaced && <Bot aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
                      {n.control && <ShieldCheck aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
                      {!n.replaced && !n.control && <FileText aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
                      {n.label}
                    </span>
                    {n.replaced && (
                      <span className="mt-1 block text-[10px] font-semibold uppercase tracking-[0.12em]" style={{ color: isSel ? "#DCE8F3" : c.blue }}>
                        Automated · manual step replaced
                      </span>
                    )}
                    {n.control && (
                      <span className="mt-1 block text-[10px] font-semibold uppercase tracking-[0.12em]" style={{ color: isSel ? "#D4ECEA" : c.teal }}>
                        Control retained
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <LegendDots items={[
        { label: "Automated step (new)", color: c.blue }, { label: "Control retained", color: c.teal },
        { label: "Previous manual process", color: c.slate, dashed: true }, { label: "Selected step", color: c.navy },
      ]} />
      <div className="mt-6 rounded-xl border border-line bg-cream/70 p-5" data-testid="automation-detail" style={{ borderLeft: `4px solid ${c.navy}` }}>
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em]" style={{ color: c.navy }}>
          {node.label}
        </p>
        <p className="mt-2 text-sm leading-relaxed text-ink">{node.detail}</p>
      </div>
      <p className="mt-4 text-xs text-muted">
        Click any step to see what it does, which manual steps it replaces and which controls are kept.
        In my experience this kind of automation removes repetitive manual steps, reduces copy-paste
        errors and frees time for the analysis itself.
      </p>
    </DashFrame>
  );
}
